import { useCallback, useEffect, useRef, useState } from 'react';
import { indexedDbDraftStore, type UnsavedDraft, type UnsavedDraftStore } from './unsavedDraftStore';

export type DraftContent = Omit<UnsavedDraft, 'keptAt'>;

/**
 * #1165 layer 2: keeps the paper's unsaved work (`draft`, null when there is none) on this device,
 * a moment after each change and as the page goes away, and clears it once it is saved or
 * discarded. A draft found on opening is offered back; until it is restored or discarded nothing
 * is written, so a fresh, empty paper never overwrites the work it holds.
 */
export function useUnsavedDraft(
  scopeKey: string,
  draft: DraftContent | null,
  store: UnsavedDraftStore = indexedDbDraftStore,
  delayMs = 2000
) {
  const [offer, setOffer] = useState<UnsavedDraft | null>(null);
  const [settled, setSettled] = useState(false);
  const latest = useRef(draft);
  useEffect(() => {
    latest.current = draft;
  }, [draft]);

  useEffect(() => {
    let live = true;
    setSettled(false);
    setOffer(null);
    void store.read(scopeKey).then((found) => {
      if (!live) return;
      if (found) setOffer(found);
      else setSettled(true);
    });
    return () => {
      live = false;
    };
  }, [scopeKey, store]);

  useEffect(() => {
    if (!settled) return;
    if (!draft) {
      void store.clear(scopeKey);
      return;
    }
    const timer = setTimeout(() => void store.write(scopeKey, { ...draft, keptAt: new Date().toISOString() }), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, draft, scopeKey, settled, store]);

  useEffect(() => {
    if (!settled) return;
    const keep = () => {
      if (latest.current) void store.write(scopeKey, { ...latest.current, keptAt: new Date().toISOString() });
    };
    window.addEventListener('pagehide', keep);
    return () => window.removeEventListener('pagehide', keep);
  }, [scopeKey, settled, store]);

  /** Puts the offered draft back (`apply`), then keeps the paper as usual. */
  const restoreOffer = useCallback(
    async (apply: (found: UnsavedDraft) => Promise<void>) => {
      if (!offer) return;
      setOffer(null);
      try {
        await apply(offer);
      } finally {
        setSettled(true);
      }
    },
    [offer]
  );

  const discardOffer = useCallback(() => {
    setOffer(null);
    setSettled(true);
    void store.clear(scopeKey);
  }, [scopeKey, store]);

  return { offer, restoreOffer, discardOffer };
}
